import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PublicLayout } from '../../../components/layout/PublicLayout';
import { supabase } from '../../../lib/supabase';
import { Event, EventAttendee } from '../../../types/event';
import { useToast } from '../../../hooks/useToast';
import { formatDate } from '../../../utils/dateFormatter';
import QRCodeScanner from '../../../components/event/QRCodeScanner';
import { checkInAttendee, getEventById, getEventAttendees, getCommissionSettings } from '../../../services/event.service';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { SEOMetaTags } from '../../../components/shared/SEOMetaTags';
import screenfull from 'screenfull';
import ScanFeedback from '../../../components/event/ScanFeedback';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

// Enregistrer les composants Chart.js nécessaires
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

// Icônes
import { 
  QrCode, 
  Users, 
  BarChart3, 
  Search, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  RefreshCw,
  Download,
  Filter,
  ChevronLeft,
  ChevronRight,
  Maximize,
  Minimize,
  Sun,
  Moon,
  XCircle
} from 'lucide-react';

const EventControl: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  // États
  const [event, setEvent] = useState<Event | null>(null);
  const [attendees, setAttendees] = useState<EventAttendee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [accessVerified, setAccessVerified] = useState<boolean>(() => {
    // Vérifier si l'accès a déjà été validé pour ce slug dans la session
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem(`event_access_${slug}`) === 'granted';
    }
    return false;
  });
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [activeTab, setActiveTab] = useState<string>('stats');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filter, setFilter] = useState<'all' | 'checked_in' | 'pending'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [refreshing, setRefreshing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true); // Par défaut en mode sombre
  const fullscreenRef = useRef<HTMLDivElement>(null);
  const [feedbackType, setFeedbackType] = useState<'success' | 'error' | 'warning' | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string>('');
  const [stats, setStats] = useState({
    total: 0,
    scanned: 0,
    pending: 0,
    percentage: 0,
    hourlyData: {} as Record<string, number>
  });

  // État pour la gestion financière
  const [financials, setFinancials] = useState({
    revenue: 0,
    ticketsSold: 0,
    avgBasket: 0,
    commissions: 0,
    platformFee: 0.05, // Taux par défaut
    paymentFee: 0.02,  // Taux par défaut
    byCategory: [],
    transactions: []
  });


  // Vérifier l'accès à l'événement
  useEffect(() => {
    const fetchEvent = async () => {
      if (!slug) {
        showToast('ID d\'événement manquant', 'error');
        navigate('/');
        return;
      }
      try {
        setLoading(true);
        const { data: eventData, error: eventError } = await supabase
          .from('events')
          .select('*')
          .eq('slug', slug)
          .single();
        if (eventError || !eventData) {
          showToast('Événement non trouvé ou slug invalide', 'error');
          navigate('/');
          return;
        }
        setEvent(eventData);
      } catch (error: any) {
        console.error('Erreur lors de la récupération de l\'événement:', error);
        showToast(error.message || 'Erreur lors de la récupération de l\'événement', 'error');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [slug, navigate, showToast]);

  // Gestion de la validation du mot de passe
  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!event) return;
    if (passwordInput === event.id) {
      setAccessVerified(true);
      setPasswordError('');
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(`event_access_${slug}`, 'granted');
      }
      window.location.reload(); // Rechargement de la page
    } else {
      setPasswordError('Mot de passe incorrect');
    }
  };

  // Si l'accès n'est pas vérifié, afficher le formulaire de mot de passe
  if (!accessVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black/90">
        <form onSubmit={handlePasswordSubmit} className="bg-white/10 p-8 rounded-lg shadow-lg w-full max-w-sm flex flex-col gap-4 border border-white/20">
          <h2 className="text-xl font-semibold text-center text-white">Accès protégé</h2>
          <p className="text-gray-300 text-center text-sm mb-2">Veuillez entrer le mot de passe pour accéder à la gestion de cet événement.</p>
          <input
            type="password"
            className="w-full px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            placeholder="Mot de passe de l'événement"
            value={passwordInput}
            onChange={e => setPasswordInput(e.target.value)}
            autoFocus
          />
          {passwordError && <div className="text-red-500 text-xs text-center">{passwordError}</div>}
          <button
            type="submit"
            className="bg-yellow-400 hover:bg-yellow-500 text-white font-semibold py-2 px-4 rounded-md transition-colors"
          >
            Accéder
          </button>
        </form>
      </div>
    );
  }

  // Calculer les données financières à partir des tickets/attendees (exemple simplifié)
  useEffect(() => {
    if (!attendees || attendees.length === 0) return;
    // Regrouper par catégorie (si info disponible)
    const categories: Record<string, { price: number, qty: number, total: number }> = {};
    let totalRevenue = 0;
    let totalTickets = 0;
    attendees.forEach(a => {
      // Récupérer les informations du ticket depuis la relation ticket_order.ticket
      const price = a.ticket_order?.ticket?.price || 0;
      const cat = a.ticket_order?.ticket?.name || 'Standard';
      if (!categories[cat]) categories[cat] = { price, qty: 0, total: 0 };
      categories[cat].qty += 1;
      categories[cat].total += price;
      totalRevenue += price;
      totalTickets += 1;
    });
    const byCategory = Object.entries(categories).map(([name, data]) => ({ name, ...data }));
    const avgBasket = totalTickets > 0 ? totalRevenue / totalTickets : 0;
    // Commissions basées sur les paramètres de la base de données
    const commissions = totalRevenue * (financials.platformFee + financials.paymentFee);
    // Historique des transactions avec type de ticket
    const transactions = attendees.map(a => ({
      date: a.created_at || new Date(),
      type: a.ticket_order?.ticket?.name || 'Standard', // Utiliser le nom du ticket
      amount: a.ticket_order?.ticket?.price || 0,       // Prix du ticket
      buyer: a.attendee_name || a.user?.name || 'Inconnu',
      status: a.status === 'checked_in' ? 'Validé' : 'En attente',
    }));
    setFinancials({
      revenue: totalRevenue,
      ticketsSold: totalTickets,
      avgBasket,
      commissions,
      byCategory,
      transactions
    });
  }, [attendees]);

  // Fonction pour récupérer les participants
  const fetchAttendees = async (eventId: string) => {
    try {
      setRefreshing(true);
      const attendeesData = await getEventAttendees(eventId);
      setAttendees(attendeesData);
      
      // Calculer les statistiques
      const total = attendeesData.length;
      const scanned = attendeesData.filter(a => a.status === 'checked_in').length;
      const pending = total - scanned;
      const percentage = total > 0 ? Math.round((scanned / total) * 100) : 0;
      
      // Calculer les données horaires pour les validations
      const hourlyData: Record<string, number> = {};
      
      attendeesData
        .filter(a => a.status === 'checked_in' && a.check_in_time)
        .forEach(a => {
          const checkInDate = new Date(a.check_in_time!);
          const hourKey = `${checkInDate.getHours()}:00`;
          
          if (hourlyData[hourKey]) {
            hourlyData[hourKey]++;
          } else {
            hourlyData[hourKey] = 1;
          }
        });
      
      setStats({
        total,
        scanned,
        pending,
        percentage,
        hourlyData
      });
    } catch (error: any) {
      console.error('Erreur lors de la récupération des participants:', error);
      showToast(error.message || 'Erreur lors de la récupération des participants', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  // Fonction pour actualiser les données
  const handleRefresh = async () => {
    if (event) {
      await fetchAttendees(event.id);
      showToast('Données actualisées', 'success');
    }
  };

  // Fonction pour gérer le scan réussi
  // Gérer les changements de mode plein écran
  useEffect(() => {
    if (screenfull.isEnabled) {
      const onChange = () => {
        setIsFullscreen(screenfull.isFullscreen);
      };
      
      screenfull.on('change', onChange);
      
      return () => {
        screenfull.off('change', onChange);
      };
    }
  }, []);

  const handleScanSuccess = async (attendee: EventAttendee) => {
    // Vérifier que le ticket appartient à cet événement
    if (attendee.event_id !== event?.id) {
      setFeedbackType('error');
      setFeedbackMessage('Ce ticket n\'appartient pas à cet événement');
      return;
    }
    
    if (attendee.status === 'checked_in') {
      setFeedbackType('warning');
      setFeedbackMessage(`Ce ticket a déjà été utilisé par ${attendee.attendee_name || attendee.user?.name || 'un participant'}`);
    } else {
      try {
        await checkInAttendee(attendee.id);
        setFeedbackType('success');
        setFeedbackMessage(`Ticket validé avec succès pour ${attendee.attendee_name || attendee.user?.name || 'le participant'}`);
        
        // Actualiser les données
        if (event) {
          await fetchAttendees(event.id);
        }
      } catch (error: any) {
        setFeedbackType('error');
        setFeedbackMessage(error.message || 'Erreur lors de la validation du ticket');
      }
    }
  };

  // Fonction de recherche avancée
  const advancedSearch = (attendee: EventAttendee, term: string) => {
    // Si le terme est vide, retourner tous les participants
    if (!term) return true;
    
    // Normaliser la recherche (supprimer les accents, mettre en minuscule)
    const normalizeText = (text: string) => text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    
    const searchTerms = normalizeText(term).split(' ').filter(t => t.length > 0);
    
    // Rechercher dans tous les champs pertinents
    const searchableFields = [
      attendee.user?.name || '',
      attendee.user?.email || '',
      attendee.attendee_name || '',
      attendee.attendee_email || '',
      attendee.qr_code || '',
      attendee.status || ''
    ];
    
    // Vérifier si tous les termes de recherche sont présents dans au moins un champ
    return searchTerms.every(term => 
      searchableFields.some(field => 
        normalizeText(field).includes(term)
      )
    );
  };
  
  // Filtrer les participants
  const filteredAttendees = attendees.filter(attendee => {
    const matchesSearch = advancedSearch(attendee, searchTerm);
    
    const matchesFilter = filter === 'all' || 
      (filter === 'checked_in' && attendee.status === 'checked_in') ||
      (filter === 'pending' && attendee.status === 'pending');
    
    return matchesSearch && matchesFilter;
  });

  // Pagination
  const totalFiltered = filteredAttendees.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / itemsPerPage));
  const paginatedAttendees = filteredAttendees.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Si la page courante dépasse le total, revenir à la dernière page
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  // Réinitialiser à la première page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm, itemsPerPage]);

  // Abonnement aux mises à jour en temps réel
  useEffect(() => {
    if (!event) return;

    const channel = supabase.channel(`event-control-${event.id}`);
    
    const subscription = channel
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'event_attendees',
        filter: `event_id=eq.${event.id}`,
      }, (payload) => {
        console.log('Changement détecté:', payload);
        // Actualiser les données
        fetchAttendees(event.id);
      })
      .subscribe((status) => {
        console.log('Statut de l\'abonnement:', status);
      });

    return () => {
      channel.unsubscribe();
    };
  }, [event]);

  useEffect(() => {
    if (event) {
      fetchAttendees(event.id);
      fetchCommissionSettings(event.id);
    }
  }, [event?.id, slug]);
  
  // Récupérer les paramètres de commission
  const fetchCommissionSettings = async (eventId: string) => {
    try {
      const commissionData = await getCommissionSettings(eventId);
      setFinancials(prev => ({
        ...prev,
        platformFee: commissionData.platform_fee,
        paymentFee: commissionData.payment_fee
      }));
    } catch (error: any) {
      console.error('Erreur lors de la récupération des paramètres de commission:', error);
      showToast(error.message || 'Erreur lors de la récupération des paramètres de commission', 'error');
    }
  };

  // Afficher l'écran de chargement
  if (loading) {
    return (
      <PublicLayout>
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
        </div>
      </PublicLayout>
    );
  }

  // Afficher l'interface principale
  return (
    <PublicLayout>
      <SEOMetaTags
        title={event ? `Contrôle - ${event.title}` : 'Contrôle d\'événement'}
        description="Interface de contrôle d'événement pour la validation des tickets"
      />
      
      {/* Composant de feedback visuel */}
      <ScanFeedback 
        type={feedbackType}
        message={feedbackMessage}
        onComplete={() => {
          setFeedbackType(null);
          setFeedbackMessage('');
        }}
        duration={4000}
      />
      
      {/* Fond avec dégradé et motif */}
      <div className="fixed inset-0 -z-10">
        <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-b from-gray-900 to-black opacity-90' : 'bg-gradient-to-b from-blue-50 to-white opacity-90'}`}></div>
        <div className="absolute inset-0 bg-[url('/images/pattern.svg')] opacity-5 bg-cover"></div>
      </div>
      
      <div className={`container mx-auto px-4 pt-20 pb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        {event && (
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {event.title}
            </h1>
            <p className="text-gray-300">
              {event.start_date && formatDate(event.start_date.toString())}
              {event.location && ` • ${event.location}`}
            </p>
          </div>
        )}
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
            <TabsTrigger 
              value="stats" 
              className="py-2 px-1 sm:px-2 rounded-md bg-yellow-400/20 text-yellow-400 data-[state=active]:bg-yellow-400 data-[state=active]:text-white transition-all text-sm sm:text-base"
            >
              <BarChart3 className="h-4 w-4 mr-1 sm:mr-2" />
              <span>Statistiques</span>
            </TabsTrigger>
            
            <TabsTrigger 
              value="scanner" 
              className="py-2 px-1 sm:px-2 rounded-md bg-blue-400/20 text-blue-400 data-[state=active]:bg-blue-500 data-[state=active]:text-white transition-all text-sm sm:text-base"
            >
              <QrCode className="h-4 w-4 mr-1 sm:mr-2" />
              <span>Scanner</span>
            </TabsTrigger>
            
            <TabsTrigger 
              value="participants" 
              className="py-2 px-1 sm:px-2 rounded-md bg-green-400/20 text-green-400 data-[state=active]:bg-green-500 data-[state=active]:text-white transition-all text-sm sm:text-base"
            >
              <Users className="h-4 w-4 mr-1 sm:mr-2" />
              <span>Participants</span>
            </TabsTrigger>
            
            <TabsTrigger 
              value="finances" 
              className="py-2 px-1 sm:px-2 rounded-md bg-purple-400/20 text-purple-400 data-[state=active]:bg-purple-500 data-[state=active]:text-white transition-all text-sm sm:text-base"
            >
              <BarChart3 className="h-4 w-4 mr-1 sm:mr-2" />
              <span>Finances</span>
            </TabsTrigger>
          </TabsList>
          
          {/* Contenu des onglets */}
          <div className={`${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-gray-900/5 border border-gray-300/10'} rounded-lg overflow-hidden`}>
            {/* Onglet Finances */}
            <TabsContent value="finances" className="p-0 m-0">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-white mb-6">Gestion financière de l'événement</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                  {/* Chiffre d'affaires */}
                  <div className="bg-white/5 rounded-lg border border-white/10 p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-300">Chiffre d'affaires</h3>
                      <BarChart3 className="h-6 w-6 text-yellow-400" />
                    </div>
                    <p className="text-3xl font-bold text-white mt-2">{financials.revenue.toLocaleString('fr-FR')} FCFA</p>
                  </div>
                  {/* Tickets vendus */}
                  <div className="bg-white/5 rounded-lg border border-white/10 p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-300">Tickets vendus</h3>
                      <Users className="h-6 w-6 text-blue-400" />
                    </div>
                    <p className="text-3xl font-bold text-white mt-2">{financials.ticketsSold}</p>
                  </div>
                  {/* Panier moyen */}
                  <div className="bg-white/5 rounded-lg border border-white/10 p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-300">Panier moyen</h3>
                      <BarChart3 className="h-6 w-6 text-green-400" />
                    </div>
                    <p className="text-3xl font-bold text-white mt-2">{financials.avgBasket.toLocaleString('fr-FR')} FCFA</p>
                  </div>
                  {/* Commissions */}
                  <div className="bg-white/5 rounded-lg border border-white/10 p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-300">Commissions</h3>
                      <BarChart3 className="h-6 w-6 text-red-400" />
                    </div>
                    <p className="text-3xl font-bold text-white mt-2">{financials.commissions.toLocaleString('fr-FR')} FCFA</p>
                    <p className="text-xs text-gray-400 mt-1">({(financials.revenue > 0 ? (financials.commissions / financials.revenue * 100) : 0).toFixed(1)}% de commission)</p>
                  </div>
                </div>
                {/* Détail par catégorie */}
                <div className="mb-8">
                  <h3 className="text-lg font-medium text-gray-300 mb-2">Tickets vendus par catégorie</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm text-white">
                      <thead>
                        <tr className="bg-white/10">
                          <th className="px-4 py-2 text-left">Catégorie</th>
                          <th className="px-4 py-2 text-left">Prix unitaire</th>
                          <th className="px-4 py-2 text-left">Quantité vendue</th>
                          <th className="px-4 py-2 text-left">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {financials.byCategory.map((cat, idx) => (
                          <tr key={idx} className="border-b border-white/10">
                            <td className="px-4 py-2">{cat.name}</td>
                            <td className="px-4 py-2">{cat.price.toLocaleString('fr-FR')} FCFA</td>
                            <td className="px-4 py-2">{cat.qty}</td>
                            <td className="px-4 py-2">{cat.total.toLocaleString('fr-FR')} FCFA</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* Historique des transactions */}
                <div className="mb-8">
                  <h3 className="text-lg font-medium text-gray-300 mb-2">Historique des transactions</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm text-white">
                      <thead>
                        <tr className="bg-white/10">
                          <th className="px-4 py-2 text-left">Date</th>
                          <th className="px-4 py-2 text-left">Type de ticket</th>
                          <th className="px-4 py-2 text-left">Prix (FCFA)</th>
                          <th className="px-4 py-2 text-left">Acheteur</th>
                          <th className="px-4 py-2 text-left">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {financials.transactions.map((tx, idx) => (
                          <tr key={idx} className="border-b border-white/10">
                            <td className="px-4 py-2">{new Date(tx.date).toLocaleString('fr-FR')}</td>
                            <td className="px-4 py-2">{tx.type}</td>
                            <td className="px-4 py-2">{tx.amount.toLocaleString('fr-FR')} FCFA</td>
                            <td className="px-4 py-2">{tx.buyer}</td>
                            <td className="px-4 py-2">{tx.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* Export CSV */}
                <button
                  onClick={() => {
                    const csvContent = [
                      ['Date', 'Type', 'Montant', 'Acheteur', 'Statut'],
                      ...financials.transactions.map(tx => [
                        new Date(tx.date).toLocaleString('fr-FR'),
                        tx.type,
                        tx.amount,
                        tx.buyer,
                        tx.status
                      ])
                    ].map(row => row.join(',')).join('\n');
                    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.setAttribute('href', url);
                    link.setAttribute('download', `transactions-${event?.title || 'evenement'}.csv`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="flex items-center px-4 py-3 bg-yellow-500/90 hover:bg-yellow-600/90 rounded-lg text-white font-bold transition-colors"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Exporter les transactions
                </button>
              </div>
            </TabsContent>
            {/* Onglet Statistiques */}
            <TabsContent value="stats" className="p-0 m-0">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-white mb-6">Statistiques de l'événement</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {/* Carte Total */}
                  <div className="bg-white/5 rounded-lg border border-white/10 p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-300">Total tickets</h3>
                      <Users className="h-6 w-6 text-blue-400" />
                    </div>
                    <p className="text-3xl font-bold text-white mt-2">{stats.total}</p>
                  </div>
                  
                  {/* Carte Validés */}
                  <div className="bg-white/5 rounded-lg border border-white/10 p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-300">Tickets validés</h3>
                      <CheckCircle className="h-6 w-6 text-green-400" />
                    </div>
                    <p className="text-3xl font-bold text-white mt-2">{stats.scanned}</p>
                  </div>
                  
                  {/* Carte En attente */}
                  <div className="bg-white/5 rounded-lg border border-white/10 p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-300">Tickets en attente</h3>
                      <Clock className="h-6 w-6 text-yellow-400" />
                    </div>
                    <p className="text-3xl font-bold text-white mt-2">{stats.pending}</p>
                  </div>
                </div>
                
                {/* Barre de progression */}
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-lg font-medium text-gray-300">Progression</h3>
                    <span className="text-white font-bold">{stats.percentage}%</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-4 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-yellow-500 to-yellow-300 h-full rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${stats.percentage}%` }}
                    ></div>
                  </div>
                </div>
                
                {/* Graphiques */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  {/* Graphique camembert */}
                  <div className="bg-white/5 rounded-lg border border-white/10 p-6">
                    <h3 className="text-lg font-medium text-gray-300 mb-4">Répartition des tickets</h3>
                    <div className="h-64 flex items-center justify-center">
                      {stats.total > 0 ? (
                        <Pie 
                          data={{
                            labels: ['Validés', 'En attente'],
                            datasets: [
                              {
                                data: [stats.scanned, stats.pending],
                                backgroundColor: [
                                  'rgba(34, 197, 94, 0.8)',  // vert
                                  'rgba(234, 179, 8, 0.8)',   // jaune
                                ],
                                borderColor: [
                                  'rgba(34, 197, 94, 1)',
                                  'rgba(234, 179, 8, 1)',
                                ],
                                borderWidth: 1,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            plugins: {
                              legend: {
                                position: 'bottom',
                                labels: {
                                  color: isDarkMode ? 'white' : 'black',
                                  font: {
                                    size: 12
                                  }
                                }
                              },
                              tooltip: {
                                backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.9)',
                                titleColor: isDarkMode ? 'white' : 'black',
                                bodyColor: isDarkMode ? 'white' : 'black',
                                displayColors: false,
                                padding: 10,
                                titleFont: {
                                  size: 14
                                },
                                bodyFont: {
                                  size: 14
                                }
                              }
                            },
                          }}
                        />
                      ) : (
                        <p className="text-gray-400">Aucune donnée disponible</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Graphique à barres */}
                  <div className="bg-white/5 rounded-lg border border-white/10 p-6">
                    <h3 className="text-lg font-medium text-gray-300 mb-4">Validations par heure</h3>
                    <div className="h-64 flex items-center justify-center">
                      {Object.keys(stats.hourlyData).length > 0 ? (
                        <Bar
                          data={{
                            labels: Object.keys(stats.hourlyData).sort(),
                            datasets: [
                              {
                                label: 'Tickets validés',
                                data: Object.keys(stats.hourlyData).sort().map(key => stats.hourlyData[key]),
                                backgroundColor: 'rgba(234, 179, 8, 0.6)',
                                borderColor: 'rgba(234, 179, 8, 1)',
                                borderWidth: 1,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            scales: {
                              y: {
                                beginAtZero: true,
                                ticks: {
                                  color: isDarkMode ? 'white' : 'black',
                                  font: {
                                    size: 12
                                  }
                                },
                                grid: {
                                  color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                                }
                              },
                              x: {
                                ticks: {
                                  color: isDarkMode ? 'white' : 'black',
                                  font: {
                                    size: 12
                                  }
                                },
                                grid: {
                                  color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                                }
                              }
                            },
                            plugins: {
                              legend: {
                                display: false
                              },
                              tooltip: {
                                backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.9)',
                                titleColor: isDarkMode ? 'white' : 'black',
                                bodyColor: isDarkMode ? 'white' : 'black',
                                displayColors: false,
                                padding: 10,
                                titleFont: {
                                  size: 14
                                },
                                bodyFont: {
                                  size: 14
                                }
                              }
                            }
                          }}
                        />
                      ) : (
                        <p className="text-gray-400">Aucune donnée de validation disponible</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="flex items-center justify-center px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/20 rounded-lg text-white transition-colors"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                    <span>Actualiser les statistiques</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      // Logique pour exporter les données
                      const csvContent = [
                        ['Nom', 'Email', 'QR Code', 'Statut', 'Date de validation'],
                        ...attendees.map(a => [
                          a.attendee_name || a.user?.name || 'Non spécifié',
                          a.attendee_email || a.user?.email || 'Non spécifié',
                          a.qr_code,
                          a.status === 'checked_in' ? 'Validé' : 'En attente',
                          a.check_in_time ? new Date(a.check_in_time).toLocaleString() : '-'
                        ])
                      ].map(row => row.join(',')).join('\n');
                      
                      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.setAttribute('href', url);
                      link.setAttribute('download', `participants-${event?.title || 'evenement'}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="flex items-center justify-center px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/20 rounded-lg text-white transition-colors"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    <span>Exporter les données</span>
                  </button>
                </div>
              </div>
            </TabsContent>
            
            {/* Onglet Scanner */}
            <TabsContent value="scanner" className="p-0 m-0">
              <div className="p-6" ref={fullscreenRef}>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-white">Scanner un ticket</h2>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setIsDarkMode(!isDarkMode)}
                      className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                      aria-label={isDarkMode ? "Passer en mode clair" : "Passer en mode sombre"}
                    >
                      {isDarkMode ? <Sun className="h-5 w-5 text-yellow-400" /> : <Moon className="h-5 w-5 text-blue-400" />}
                    </button>
                    <button 
                      onClick={() => {
                        if (screenfull.isEnabled) {
                          if (!isFullscreen) {
                            screenfull.request(fullscreenRef.current as Element).then(() => {
                              setIsFullscreen(true);
                            });
                          } else {
                            screenfull.exit().then(() => {
                              setIsFullscreen(false);
                            });
                          }
                        }
                      }}
                      className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                      aria-label={isFullscreen ? "Quitter le mode plein écran" : "Passer en plein écran"}
                    >
                      {isFullscreen ? 
                        <Minimize className="h-5 w-5 text-white" /> : 
                        <Maximize className="h-5 w-5 text-white" />}
                    </button>
                  </div>
                </div>
                <QRCodeScanner onSuccess={handleScanSuccess} />
              </div>
            </TabsContent>
            
            {/* Onglet Participants */}
            <TabsContent value="participants" className="p-0 m-0">
              <div className="p-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                  <h2 className="text-xl font-semibold text-white">Liste des participants</h2>
                  
                  <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                    {/* Barre de recherche avancée */}
                    <div className="relative flex-grow">
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Rechercher par nom, email, QR code ou statut..."
                        className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-transparent pl-10"
                      />
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      {searchTerm && (
                        <button 
                          onClick={() => setSearchTerm('')}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-white/10"
                          aria-label="Effacer la recherche"
                        >
                          <XCircle className="h-4 w-4 text-gray-400" />
                        </button>
                      )}
                      <div className="absolute left-0 right-0 top-full mt-1 z-10">
                        {searchTerm && filteredAttendees.length > 0 && filteredAttendees.length < attendees.length && (
                          <div className="bg-gray-800 border border-white/10 rounded-md shadow-lg p-2 text-xs text-gray-300">
                            {filteredAttendees.length} résultat{filteredAttendees.length > 1 ? 's' : ''} trouvé{filteredAttendees.length > 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Filtre */}
                    <select
                      value={filter}
                      onChange={(e) => setFilter(e.target.value as any)}
                      className="px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-transparent"
                    >
                      <option value="all" className="bg-gray-900">Tous</option>
                      <option value="checked_in" className="bg-gray-900">Validés</option>
                      <option value="pending" className="bg-gray-900">En attente</option>
                    </select>
                    
                    {/* Bouton actualiser */}
                    <button
                      onClick={handleRefresh}
                      disabled={refreshing}
                      className="flex items-center justify-center px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/20 rounded-lg text-white transition-colors"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                      <span>Actualiser</span>
                    </button>
                  </div>
                </div>
                
                {/* Liste des participants */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-white/5 border-b border-white/10">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Nom</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">QR Code</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Statut</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {paginatedAttendees.length > 0 ? (
                        paginatedAttendees.map((attendee) => (
                          <tr key={attendee.id} className="hover:bg-white/5">
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                              {attendee.attendee_name || attendee.user?.name || 'Non spécifié'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                              {attendee.attendee_email || attendee.user?.email || 'Non spécifié'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300 font-mono">
                              {attendee.qr_code}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                attendee.status === 'checked_in' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {attendee.status === 'checked_in' ? (
                                  <>
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Validé
                                  </>
                                ) : (
                                  <>
                                    <Clock className="w-3 h-3 mr-1" />
                                    En attente
                                  </>
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                              {attendee.check_in_time 
                                ? new Date(attendee.check_in_time).toLocaleString('fr-FR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                : '-'
                              }
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                            Aucun participant trouvé
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {totalFiltered > 0 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
                    <div className="text-sm text-gray-400">
                      Affichage de {Math.min((currentPage - 1) * itemsPerPage + 1, totalFiltered)} à {Math.min(currentPage * itemsPerPage, totalFiltered)} sur {totalFiltered} participants
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <select
                        value={itemsPerPage}
                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                        className="px-2 py-1 bg-white/5 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-transparent"
                      >
                        {[10, 20, 50, 100].map(value => (
                          <option key={value} value={value} className="bg-gray-900">
                            {value} par page
                          </option>
                        ))}
                      </select>
                      
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="p-1 rounded hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="h-5 w-5 text-white" />
                        </button>
                        
                        <span className="text-white px-2">
                          {currentPage} / {totalPages}
                        </span>
                        
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="p-1 rounded hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRight className="h-5 w-5 text-white" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </PublicLayout>
  );
};

export default EventControl;
